import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoChevronDown, IoClose, IoSearch } from "react-icons/io5";
import { Country, State, City } from "country-state-city";

const CareOfFilters = ({
  filters,
  onFilterChange,
  onFiltersChange,
  onClearFilters,
  uniqueStatuses,
  uniqueSource,
  showSearch = true,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchByDropdown, setSearchByDropdown] = useState({});
  const dropdownRef = useRef();

  const countryOptions = useMemo(
    () => Country.getAllCountries().map((country) => country.name),
    [],
  );
  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((country) => country.name === filters.country),
    [filters.country],
  );
  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return State.getStatesOfCountry(selectedCountry.isoCode).map((state) => state.name);
  }, [selectedCountry]);
  const selectedState = useMemo(() => {
    if (!selectedCountry || !filters.state) return null;
    return State.getStatesOfCountry(selectedCountry.isoCode).find(
      (state) => state.name === filters.state,
    );
  }, [selectedCountry, filters.state]);
  const districtOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
    return Array.from(new Set(cities.map((city) => city.name))).sort();
  }, [selectedCountry, selectedState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (key, disabled = false) => {
    if (disabled) return;
    setOpenDropdown((prev) => (prev === key ? null : key));
    setSearchByDropdown((prev) => ({ ...prev, [key]: "" }));
  };

  const getFilteredOptions = (key, options) => {
    const term = (searchByDropdown[key] || "").trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.toLowerCase().includes(term));
  };

  const renderDropdown = ({ keyName, label, value, options, onSelect, onClear, disabled }) => {
    const filteredOptions = getFilteredOptions(keyName, options);

    return (
      <div className="relative w-44">
        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
          {label}
        </label>
        <button
          type="button"
          onClick={() => toggleDropdown(keyName, disabled)}
          className={`flex h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm dark:text-white ${
            disabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-gray-800/70 dark:text-gray-500"
              : "border-gray-200 bg-white hover:border-gray-300 dark:border-white/10 dark:bg-white/5"
          }`}
        >
          <span className="truncate">{value || `Select ${label.toLowerCase()}`}</span>
          <IoChevronDown className="h-4 w-4 text-gray-500" />
        </button>

        {openDropdown === keyName && !disabled && (
          <div className="absolute top-full z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <input
              type="text"
              value={searchByDropdown[keyName] || ""}
              onChange={(e) =>
                setSearchByDropdown((prev) => ({ ...prev, [keyName]: e.target.value }))
              }
              placeholder={`Search ${label.toLowerCase()}`}
              className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
            />
            <button
              type="button"
              onClick={() => {
                onClear();
                setOpenDropdown(null);
              }}
              className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Clear
            </button>
            <div className="max-h-44 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No options found</p>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    type="button"
                    key={option}
                    onClick={() => {
                      onSelect(option);
                      setOpenDropdown(null);
                    }}
                    className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      value === option
                        ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                        : "text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {option}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className="mt-2 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800"
    >
      <div className="flex flex-wrap items-end justify-center gap-3">
        {showSearch && (
          <div className="w-full md:w-56">
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              Search
            </label>
            <div className="relative">
              <IoSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={filters.search || ""}
                onChange={(e) => onFilterChange("search", e.target.value)}
                placeholder="Name, phone, email, company"
                className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </div>
          </div>
        )}

        {renderDropdown({
          keyName: "country",
          label: "Country",
          value: filters.country,
          options: countryOptions,
          onSelect: (option) => {
            if (onFiltersChange) {
              onFiltersChange({
                ...filters,
                country: option,
                state: "",
                district: "",
              });
              return;
            }
            onFilterChange("country", option);
            onFilterChange("state", "");
            onFilterChange("district", "");
          },
          onClear: () => {
            if (onFiltersChange) {
              onFiltersChange({
                ...filters,
                country: "",
                state: "",
                district: "",
              });
              return;
            }
            onFilterChange("country", "");
            onFilterChange("state", "");
            onFilterChange("district", "");
          },
          disabled: false,
        })}

        {renderDropdown({
          keyName: "state",
          label: "State",
          value: filters.state,
          options: stateOptions,
          onSelect: (option) => {
            if (onFiltersChange) {
              onFiltersChange({
                ...filters,
                state: option,
                district: "",
              });
              return;
            }
            onFilterChange("state", option);
            onFilterChange("district", "");
          },
          onClear: () => {
            if (onFiltersChange) {
              onFiltersChange({
                ...filters,
                state: "",
                district: "",
              });
              return;
            }
            onFilterChange("state", "");
            onFilterChange("district", "");
          },
          disabled: !filters.country,
        })}

        {renderDropdown({
          keyName: "district",
          label: "District",
          value: filters.district,
          options: districtOptions,
          onSelect: (option) => onFilterChange("district", option),
          onClear: () => onFilterChange("district", ""),
          disabled: !filters.state,
        })}

        {renderDropdown({
          keyName: "source",
          label: "Source",
          value: filters.source,
          options: uniqueSource,
          onSelect: (option) => onFilterChange("source", option),
          onClear: () => onFilterChange("source", ""),
          disabled: false,
        })}

        {renderDropdown({
          keyName: "status",
          label: "Status",
          value: filters.status,
          options: uniqueStatuses,
          onSelect: (option) => onFilterChange("status", option),
          onClear: () => onFilterChange("status", ""),
          disabled: false,
        })}

        <div className="w-44">
          <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
            Actions
          </label>
          <button
            onClick={onClearFilters}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            <IoClose className="h-4 w-4" />
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareOfFilters;
